// src/Signup.js
import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";

const Signup = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("founder");
  const [isCertified, setIsCertified] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("ZAR"); // default fallback

  const navigate = useNavigate();

  // 🌍 Detect user currency automatically
  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        if (data && data.currency) {
          setCurrency(data.currency); // e.g., USD, EUR, ZAR
        }
      } catch (err) {
        console.error("Currency detection failed:", err);
      }
    };
    fetchCurrency();
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }

    if (role === "investor" && !isCertified) {
      setError("Please confirm that you are a sophisticated investor before signing up.");
      return;
    }

    if (role === "founder" && !agreedTerms) {
      setError("You must agree to the Terms of Use before signing up.");
      return;
    }

    try {
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (fullName) {
        await updateProfile(user, { displayName: fullName });
      }

      // Save to Firestore
      await setDoc(doc(db, "users", user.uid), {
        fullName,
        email,
        role,
        createdAt: serverTimestamp(),
        isLookingForDeals: role === "investor" ? false : null,
        certified: role === "investor" ? true : null,
        agreedTerms: role === "founder" ? true : null,
        totalCommission: 0,
        currency: role === "investor" ? currency : null, // 👈 only investors get auto currency
      });

      // ✅ Send verification email before allowing login
      await sendEmailVerification(user);

      alert("📩 Account created! Please verify your email before logging in.");
      navigate("/verify-email");
    } catch (err) {
      console.error("Signup Error:", err);

      if (err.code === "auth/email-already-in-use") {
        setError("This email is already registered. Please log in instead.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (err.code === "auth/weak-password") {
        setError("Password should be at least 12 characters.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-4">📝 Sign Up</h2>
      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

      <form onSubmit={handleSignup} className="space-y-4">
        <input
          type="text"
          placeholder="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          required
        />
        <input
          type="password"
          placeholder="Password (min 12 chars)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          required
        />

        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full border px-3 py-2 rounded"
        >
          <option value="founder">💰 Entrepreneur</option>
          <option value="investor">💼 Investor</option>
        </select>

        {role === "investor" && (
          <div className="text-sm text-gray-700">
            <label className="inline-flex items-start gap-2">
              <input
                type="checkbox"
                checked={isCertified}
                onChange={(e) => setIsCertified(e.target.checked)}
                className="mt-1"
              />
              I certify that I am a real, sophisticated investor capable of making informed investment decisions. ✅
            </label>
            <p className="mt-1 text-xs text-gray-500">
              🌍 Detected Currency: <strong>{currency}</strong>
            </p>
          </div>
        )}

        {role === "founder" && (
          <div className="text-sm text-gray-700 space-y-2">
            <div className="border p-3 h-40 overflow-y-scroll bg-gray-50 rounded">
              <p><strong>Terms of Use:</strong></p>
              <p>By creating an account on InvestorLanes, you agree to comply with these Terms of Use. Please read them carefully.</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Use InvestorLanes for legitimate business purposes only.</li>
                <li>Provide accurate, complete, and truthful information.</li>
                <li>Respect other users' privacy and communication preferences.</li>
                <li>Do not engage in harassment, threats, or illegal activities.</li>
                <li>InvestorLanes is not responsible for funding outcomes or guarantees.</li>
                <li>You must comply with all applicable laws and regulations.</li>
                <li>Violation may result in suspension or termination of your account.</li>
              </ul>
              <p className="mt-2">By checking the box below, you confirm that you have read and agree to these Terms of Use.</p>
            </div>
            <label className="inline-flex items-start gap-2 mt-2">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-1"
              />
              I have read and agree to the Terms of Use. ✅
            </label>
          </div>
        )}

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 flex justify-center items-center"
          disabled={loading}
        >
          {loading ? "⏳ Creating Account..." : "🔐 Create Account"}
        </button>
      </form>
    </div>
  );
};

export default Signup;
